<template>
  <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <div>
          <h3 class="modal-title">Add Balance</h3>
          <p class="modal-subtitle">Select Top Up Method</p>
        </div>
        <button class="modal-close" @click="$emit('close')">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6 6L18 18" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <div class="modal-content-wrapper">
          <!-- Left panel: Bank selection -->
          <div class="bank-methods-container">
            <div class="bank-methods">
              <!-- Loading state -->
              <div v-if="isLoadingBanks" class="loading-banks">
                <div class="loading-spinner"></div>
                <span>Loading banks...</span>
              </div>
              <!-- Bank list -->
              <div
                v-else
                v-for="(bank, idx) in banks"
                :key="bank.id"
                class="bank-method"
                :class="{ 
                  selected: idx === selectedBankIndex,
                  disabled: !bank.vaAvailable 
                }"
                @click="bank.vaAvailable ? selectBank(idx) : null"
              >
                <img :src="bank.logo" :alt="bank.name" class="bank-logo" />
                <div class="bank-info">
                  <span class="bank-name">{{ bank.name }}</span>
                  <span v-if="!bank.vaAvailable" class="not-available-badge">Not Available</span>
                </div>
                <svg v-if="bank.vaAvailable" class="bank-chevron" width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- Right panel: VA details and instructions -->
          <div class="balance-details-container">
            <div class="balance-details">
              <!-- VA Card -->
              <div class="va-card" v-if="banks[selectedBankIndex] && banks[selectedBankIndex].vaAvailable">
                <img :src="banks[selectedBankIndex].logo" class="va-bank-logo" />
                <div class="va-info">
                  <div class="va-account-name">Account Name</div>
                  <div class="va-merchant-name">{{ companyName || 'Merchant Company Name' }}</div>
                  <div class="va-label">No. Virtual Account</div>
                  <div class="va-number-row">
                    <span class="va-number">{{ currentVaNumber }}</span>
                    <button class="copy-btn" @click="copyVA">Copy</button>
                  </div>
                </div>
              </div>

              <!-- Not Available Message -->
              <div v-else-if="banks[selectedBankIndex] && !banks[selectedBankIndex].vaAvailable" class="not-available-card">
                <div class="not-available-content">
                  <svg class="not-available-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#EF4444" stroke-width="2"/>
                    <path d="m15 9-6 6" stroke="#EF4444" stroke-width="2"/>
                    <path d="m9 9 6 6" stroke="#EF4444" stroke-width="2"/>
                  </svg>
                  <h4>Virtual Account Not Available</h4>
                  <p>This bank is currently not available for virtual account top-up. Please select another bank.</p>
                </div>
              </div>

              <!-- How to Pay -->
              <div class="how-to-pay" v-if="banks[selectedBankIndex] && banks[selectedBankIndex].vaAvailable">
                <h4 class="how-to-pay-title">How to pay</h4>
                <div class="accordion">
                  <div
                    v-for="(tab, idx) in howToPayTabs"
                    :key="tab"
                    class="accordion-item"
                  >
                    <div class="accordion-header" @click="toggleAccordion(idx)">
                      <span>{{ tab }}</span>
                      <svg class="accordion-chevron" :class="{ rotated: openAccordionItems.includes(idx) }" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                         <path d="M5 7.5L10 12.5L15 7.5" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <transition name="accordion">
                      <div v-show="openAccordionItems.includes(idx)" class="accordion-content">
                        <ul>
                          <li v-for="(step, stepIdx) in currentHowToPaySteps(tab)" :key="`${tab}-${stepIdx}`">
                            {{ step }}
                          </li>
                        </ul>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getBanks } from '@/api/transfer';
import { getListVA } from '@/api/balance';

export default {
  name: 'AddBalanceModal',
  props: {
    show: Boolean
  },
  data() {
    return {
      selectedBankIndex: 0,
      openAccordionItems: [0], // First tab open by default
      vaNumber: '1111-1111-1111',
      companyName: '',
      isLoadingBanks: false,
      
      // Bank data - now will be populated from API
      banks: [],
      
      // VA data from list-va API
      vaList: [],
      
      // How to pay data with optimized bank instructions
      howToPayTabs: ['ATM', 'Mobile Banking'],
      howToPay: {
        mandiri: {
          ATM: [
            'Buka Bank ATM Mandiri',
            'Masuk ke menu Bayar',
            'Cari I-PAY atau ketik 70017',
            'Masukkan no VA di no ref code',
            'Masukkan amount dan bayar'
          ],
          'Mobile Banking': [
            'Buka Livin Mandiri',
            'Masuk ke menu Bayar',
            'Cari I-PAY atau ketik 70017',
            'Masukkan no VA di no ref code',
            'Masukkan amount dan bayar'
          ]
        },
        permata: {
          ATM: [
            'Masukkan kartu ATM dan PIN',
            'Pilih menu "Transfer"',
            'Pilih "Transfer ke Bank Lain" atau "Transfer Antar Bank"',
            'Pilih Bank Permata sebagai bank tujuan',
            'Masukkan nomor VA yang tertera',
            'Masukkan jumlah yang ingin ditransfer',
            'Konfirmasi transaksi dan selesai'
          ],
          'Mobile Banking': [
            'Buka aplikasi Mobile Banking',
            'Pilih menu "Transfer"',
            'Pilih "Transfer ke Bank Lain"',
            'Pilih Bank Permata sebagai bank tujuan',
            'Masukkan nomor VA yang tertera',
            'Masukkan jumlah yang ingin ditransfer',
            'Konfirmasi transaksi dan selesai'
          ]
        },
        // For non-Mandiri and non-Permata banks - transfer to Mandiri account with 88017 prefix
        other: {
          ATM: [
            'Masukkan kartu ATM dan PIN',
            'Pilih menu "Transfer"',
            'Pilih "Transfer ke Bank Lain" atau "Transfer Antar Bank"',
            'Pilih Bank Mandiri sebagai bank tujuan',
            'Masukkan nomor rekening: 88017 + nomor VA (contoh: 88017160231XXXX)',
            'Masukkan jumlah yang ingin ditransfer',
            'Konfirmasi transaksi dan selesai'
          ],
          'Mobile Banking': [
            'Buka aplikasi Mobile Banking',
            'Pilih menu "Transfer"',
            'Pilih "Transfer ke Bank Lain"',
            'Pilih Bank Mandiri sebagai bank tujuan',
            'Masukkan nomor rekening: 88017 + nomor VA (contoh: 88017160231XXXX)',
            'Masukkan jumlah yang ingin ditransfer',
            'Konfirmasi transaksi dan selesai'
          ]
        }
      }
    };
  },
  computed: {
    // Get current VA number based on selected bank
    currentVaNumber() {
      if (!this.banks[this.selectedBankIndex] || !this.banks[this.selectedBankIndex].vaAvailable) {
        return this.vaNumber; // fallback
      }
      
      const selectedBank = this.banks[this.selectedBankIndex];
      const vaData = this.vaList.find(va => va.bank_id === selectedBank.id);
      const baseVaNumber = vaData ? vaData.no_va : this.vaNumber;
      
      // Check if bank is Mandiri
      const isMandiri = selectedBank.name.toLowerCase().includes('mandiri');
      const isPermata = selectedBank.name.toLowerCase().includes('permata');
      
      // For non-Mandiri banks, add 88017 prefix
      if (!isMandiri && !isPermata) {
        return `88017${baseVaNumber}`;
      }
      
      return baseVaNumber;
    }
  },
  methods: {
    // Fetch banks from API
    async fetchBanks() {
      this.isLoadingBanks = true;
      try {
        const response = await getBanks({ is_topup_bank: true });
        if (response && response.status === 'success' && Array.isArray(response.data)) {
          // Map API response to the expected format
          this.banks = response.data.map(bank => ({
            id: bank.id,
            name: bank.bank_name,
            logo: this.getDefaultLogo(bank.bank_name),
            vaAvailable: false // Will be updated after fetching VA list
          }));
          
          // After getting banks, fetch VA list and update availability
          await this.fetchVAList();
        } else {
          console.error('Failed to load banks:', response?.message || 'Unknown error');
          // Fallback to default banks if API fails
          this.setDefaultBanks();
        }
      } catch (error) {
        console.error('Error fetching banks:', error);
        // Fallback to default banks if API fails
        this.setDefaultBanks();
      } finally {
        this.isLoadingBanks = false;
      }
    },

    // Fetch VA list from API
    async fetchVAList() {
      try {
        const response = await getListVA();
        if (response && response.status === 'success' && Array.isArray(response.data)) {
          this.vaList = response.data;
          
          // Update bank availability based on VA list
          this.updateBankAvailability();
          
          // Ensure selected bank is available, if not select first available
          this.ensureSelectedBankIsAvailable();
        } else {
          console.error('Failed to load VA list:', response?.message || 'Unknown error');
          // If VA list fails, mark all banks as available (fallback)
          this.banks.forEach(bank => {
            bank.vaAvailable = true;
          });
        }
      } catch (error) {
        console.error('Error fetching VA list:', error);
        // If VA list fails, mark all banks as available (fallback)
        this.banks.forEach(bank => {
          bank.vaAvailable = true;
        });
      }
    },

    // Update bank availability based on VA list
    updateBankAvailability() {
      this.banks.forEach(bank => {
        const hasVA = this.vaList.some(va => va.bank_id === bank.id);
        bank.vaAvailable = hasVA;
      });
    },

    // Ensure selected bank is available
    ensureSelectedBankIsAvailable() {
      const currentBank = this.banks[this.selectedBankIndex];
      if (!currentBank || !currentBank.vaAvailable) {
        // Find first available bank
        const availableBankIndex = this.banks.findIndex(bank => bank.vaAvailable);
        if (availableBankIndex !== -1) {
          this.selectedBankIndex = availableBankIndex;
        }
      }
    },

    // Fallback method to set default banks
    setDefaultBanks() {
      this.banks = [
        { id: 1, name: 'Bank Central Asia (BCA)', logo: require('@/assets/img/logos/bank-bca.png'), vaAvailable: true },
        { id: 2, name: 'Bank Rakyat Indonesia (BRI)', logo: require('@/assets/img/logos/bank-bri.png'), vaAvailable: true },
        { id: 3, name: 'Bank Mandiri', logo: require('@/assets/img/logos/bank-mandiri.png'), vaAvailable: true },
        { id: 4, name: 'Bank Negara Indonesia (BNI)', logo: require('@/assets/img/logos/bank-bni.png'), vaAvailable: true },
        { id: 5, name: 'Bank Permata', logo: require('@/assets/img/logos/bank-permata.png'), vaAvailable: true },
      ];
    },

    // Get default logo based on bank name
    getDefaultLogo(bankName) {
      const logoMap = {
        'Bank Central Asia': require('@/assets/img/logos/bank-bca.png'),
        'Bank Rakyat Indonesia': require('@/assets/img/logos/bank-bri.png'),
        'Bank Mandiri': require('@/assets/img/logos/bank-mandiri.png'),
        'Bank Negara Indonesia': require('@/assets/img/logos/bank-bni.png'),
        'Bank Permata': require('@/assets/img/logos/bank-permata.png'),
      };
      
      // Try to match with or without prefixes
      let matchedLogo = logoMap[bankName];
      
      if (!matchedLogo) {
        // Try to find by checking if bank name contains key words
        for (const [key, logo] of Object.entries(logoMap)) {
          if (bankName.toLowerCase().includes(key.toLowerCase()) || 
              key.toLowerCase().includes(bankName.toLowerCase())) {
            matchedLogo = logo;
            break;
          }
        }
      }
      
      return matchedLogo || require('@/assets/img/logos/bank-bca.png'); // Default to BCA if not found
    },

    // Select bank and reset accordion
    selectBank(index) {
      // Only allow selection if bank is available
      if (this.banks[index] && this.banks[index].vaAvailable) {
        this.selectedBankIndex = index;
        this.openAccordionItems = [0]; // Reset to first accordion item
      }
    },
    
    // Copy VA number
    copyVA() {
      const vaToCopy = this.currentVaNumber;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(vaToCopy)
          .then(() => {
            console.log('VA Copied!');
          })
          .catch(err => {
            console.error('Failed to copy VA: ', err);
          });
      } else {
        console.warn('Clipboard API not available.');
      }
    },
    
    // Toggle accordion
    toggleAccordion(index) {
      const itemIndex = this.openAccordionItems.indexOf(index);
      if (itemIndex > -1) {
        this.openAccordionItems.splice(itemIndex, 1);
      } else {
        this.openAccordionItems.push(index);
      }
    },
    
    // Get steps for current bank and payment method
    currentHowToPaySteps(tabName) {
      const selectedBank = this.banks[this.selectedBankIndex];
      if (!selectedBank) {
        return ['Instruction data not available.'];
      }
      
      // Check if bank is Mandiri or Permata
      const isMandiri = selectedBank.name.toLowerCase().includes('mandiri');
      const isPermata = selectedBank.name.toLowerCase().includes('permata');
      
      if (isMandiri) {
        // Use Mandiri specific instructions
        return this.howToPay.mandiri[tabName] || [`Instructions for ${tabName} not available.`];
      } else if (isPermata) {
        // Use Permata specific instructions
        return this.howToPay.permata[tabName] || [`Instructions for ${tabName} not available.`];
      } else {
        // Use generic transfer instructions for non-Mandiri and non-Permata banks
        const steps = this.howToPay.other[tabName] || [`Instructions for ${tabName} not available.`];
        
        // Replace placeholder with actual VA number for better clarity
        return steps.map(step => {
          if (step.includes('88017 + nomor VA')) {
            const actualVA = this.currentVaNumber;
            return step.replace('88017 + nomor VA (contoh: 88017160231XXXX)', `88017${actualVA}`);
          }
          return step;
        });
      }
    },

    // Load company name from localStorage
    loadCompanyName() {
      this.companyName = localStorage.getItem('merchantName') || '';
      // this.companyName = 'PT Anugrah Multi Remitance';
    }
  },
  
  async mounted() {
    await this.fetchBanks();
    this.loadCompanyName();
  }
}
</script>

<style scoped>
/* Modal Base */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  background-size: cover; /* Prevent stretching */
  background-repeat: no-repeat; /* Prevent tiling */
  background-position: center; /* Center the background */
}

.modal-content {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 960px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

/* Header */
.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0; /* Prevent header from shrinking */
}

.modal-title {
  font-size: 22px;
  font-weight: bold;
  color: #111827;
  margin: 0 0 4px 0;
}

.modal-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
}

/* Body - Scrollable content area */
.modal-body {
  flex: 1;
  overflow-y: auto; /* Enable scrolling for the main content area */
  position: relative;
  height: calc(90vh - 80px); /* Adjust based on your header height */
}

.modal-content-wrapper {
  display: flex;
  height: 100%;
}

/* Left panel - Bank methods */
.bank-methods-container {
  flex: 0 0 280px;
  border-right: 1px solid #e5e7eb;
  height: 100%;
}

.bank-methods {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
}

.bank-method {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  transition: border-color 0.2s ease;
}

.bank-method:hover {
  border-color: #d1d5db;
}

.bank-method.selected {
  border-color: #34a853;
  box-shadow: 0 0 0 1px #34a853;
}

.bank-method.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.bank-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.bank-info {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bank-name {
  flex-grow: 1;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.not-available-badge {
  background-color: #ef4444;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.bank-chevron {
  color: #9ca3af;
}

/* Loading state */
.loading-banks {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 16px;
  color: #6b7280;
  font-size: 14px;
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #34a853;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Right panel - Account details and instructions */
.balance-details-container {
  flex: 1;
  height: 100%;
}

.balance-details {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
}

/* VA Card */
.va-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  border: 1px solid #e5e7eb;
}

.va-bank-logo {
  width: 60px;
  height: 60px;
  object-fit: contain;
}

.va-info {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.va-account-name {
  font-size: 14px;
  color: #6b7280;
}

.va-merchant-name {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.va-label {
  color: #6b7280;
  font-size: 14px;
  margin-top: 8px;
}

.va-number-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.va-number {
  font-size: 18px;
  font-family: 'Courier New', monospace;
  font-weight: 600;
  color: #111827;
  letter-spacing: 1px;
}

.copy-btn {
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #34a853;
  cursor: pointer;
  padding: 0;
}

.copy-btn:hover {
  text-decoration: underline;
}

/* Not Available Message */
.not-available-card {
  background-color: #fef2f2;
  border-radius: 12px;
  padding: 32px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fecaca;
}

.not-available-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 300px;
}

.not-available-content h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #dc2626;
}

.not-available-content p {
  margin: 0;
  font-size: 14px;
  color: #7f1d1d;
  line-height: 1.5;
}

.not-available-icon {
  width: 48px;
  height: 48px;
}

.how-to-pay {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.how-to-pay-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
}

/* Accordion */
.accordion-item {
  border-bottom: 1px solid #e5e7eb;
}

.accordion-item:last-child {
  border-bottom: none;
}

.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  cursor: pointer;
  background-color: #fff;
  transition: background-color 0.2s ease;
}

.accordion-header:hover {
  background-color: #f9fafb;
}

.accordion-header span {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.accordion-chevron {
  transition: transform 0.3s ease;
}

.accordion-chevron.rotated {
  transform: rotate(180deg);
}

.accordion-content {
  padding: 0px 24px 16px 24px;
  background-color: #f9fafb;
}

.accordion-content ul {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
}

.accordion-content ul li {
  margin-bottom: 4px;
}

/* Accordion animations */
.accordion-enter-active, .accordion-leave-active {
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
  max-height: 300px;
  overflow: hidden;
  opacity: 1;
}

.accordion-enter-from, .accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>